import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle, Info } from "lucide-react";
import { screenPrintingTiers, dtfTiers, embroideryTiers, rushPricing, pricingNotes } from "../data/pricing";
import { useState } from "react";
import QuoteForm from "../components/QuoteForm";
import QuoteCalculator from "../components/QuoteCalculator";

const tabs = ["Screen Printing", "DTF Printing", "Embroidery"];

export default function Pricing() {
  const [activeTab, setActiveTab] = useState(0);
  const [showForm, setShowForm] = useState(false);

  const pricingSchema = {
    "@context": "https://schema.org",
    "@type": "PriceSpecification",
    "name": "Custom Screen Printing Pricing Dallas TX",
    "description": "Screen printing, DTF printing, and embroidery pricing in Dallas TX. Volume discounts from 6 to 288+ pieces.",
    "priceCurrency": "USD",
    "eligibleRegion": "Dallas, TX",
    "seller": { "@type": "LocalBusiness", "name": "Ninja Screen Print" }
  };

  return (
    <>
      <Helmet>
        <title>Custom Screen Printing Prices Dallas TX | 2025 Pricing Guide | Ninja Screen Print</title>
        <meta name="description" content="Screen printing, DTF, and embroidery pricing in Dallas TX. Starting from $5.00/piece. Volume discounts, rush pricing, and bulk order rates. Free quote in 2 hours." />
        <script type="application/ld+json">{JSON.stringify(pricingSchema)}</script>
      </Helmet>

      {/* Hero */}
      <div className="pt-16 bg-[#0d0d0d] border-b border-neutral-800">
        <div className="ninja-container py-16">
          <div className="red-line" />
          <h1 className="font-teko text-5xl sm:text-6xl text-white uppercase tracking-tight">
            2025 Pricing Guide
          </h1>
          <p className="text-neutral-400 font-inter mt-3 text-lg max-w-2xl">
            Transparent, competitive pricing for screen printing, DTF, and embroidery in Dallas TX. No hidden fees. No surprise charges.
          </p>
          <div className="flex flex-wrap gap-4 mt-6">
            {["No setup fees for 24+ pieces", "Free DFW delivery on 24+ pcs", "Free art review on all orders"].map((item) => (
              <div key={item} className="flex items-center gap-2 text-sm text-neutral-300 font-inter">
                <CheckCircle size={12} className="text-red-500" /> {item}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Instant Quote Calculator */}
      <section className="ninja-section bg-[#0d0d0d] border-b border-neutral-800">
        <div className="ninja-container">
          <QuoteCalculator />
        </div>
      </section>

      {/* Tabs */}
      <div className="sticky top-16 z-40 bg-[#0A0A0A]/95 backdrop-blur border-b border-neutral-800">
        <div className="ninja-container flex gap-0">
          {tabs.map((tab, i) => (
            <button
              key={tab}
              data-testid={`pricing-tab-${i}`}
              onClick={() => setActiveTab(i)}
              className={`px-6 py-4 text-sm font-teko uppercase tracking-widest transition-colors ${
                activeTab === i
                  ? "text-white border-b-2 border-red-600"
                  : "text-neutral-500 hover:text-white"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      <section className="ninja-section">
        <div className="ninja-container">
          <div className="grid lg:grid-cols-3 gap-10">
            {/* Pricing Table */}
            <div className="lg:col-span-2">
              {/* Screen Printing */}
              {activeTab === 0 && (
                <div data-testid="screen-printing-pricing">
                  <h2 className="font-teko text-3xl text-white uppercase tracking-tight mb-2">Screen Printing Pricing</h2>
                  <p className="text-neutral-400 font-inter text-sm mb-6">1-color design on standard t-shirt. Additional colors add per-piece cost.</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="pricing-table">
                      <thead>
                        <tr className="border-b border-neutral-800">
                          <th className="text-left py-3 pr-4 text-xs text-neutral-500 uppercase tracking-widest font-inter">Quantity</th>
                          <th className="text-left py-3 pr-4 text-xs text-neutral-500 uppercase tracking-widest font-inter">Per Piece (1 color)</th>
                          <th className="text-left py-3 text-xs text-neutral-500 uppercase tracking-widest font-inter">Add'l Color</th>
                        </tr>
                      </thead>
                      <tbody>
                        {screenPrintingTiers.map((tier) => (
                          <tr
                            key={tier.range}
                            className={`border-b border-neutral-900 transition-colors hover:bg-neutral-900 ${tier.popular ? "pricing-popular bg-red-900/10 border-red-900/30" : ""}`}
                          >
                            <td className="py-4 pr-4">
                              <span className="font-teko text-xl text-white">{tier.range} <span className="text-neutral-500 font-inter text-sm font-normal">pcs</span></span>
                            </td>
                            <td className="py-4 pr-4">
                              <span className="font-teko text-2xl text-red-500">{tier.perPiece}</span>
                            </td>
                            <td className="py-4">
                              <span className="text-neutral-400 font-inter text-sm">{tier.addlColor}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-6 p-4 bg-neutral-900/50 border border-neutral-800 text-xs text-neutral-500 font-inter">
                    <strong className="text-neutral-300">Note:</strong> Pricing shown is for printing only. Blank garments are additional (Gildan 5000 from $2.50/pc). Screen setup fees apply for orders under 24 pieces ($20/screen/color).
                  </div>
                </div>
              )}

              {/* DTF Pricing */}
              {activeTab === 1 && (
                <div data-testid="dtf-pricing">
                  <h2 className="font-teko text-3xl text-white uppercase tracking-tight mb-2">DTF Printing Pricing</h2>
                  <p className="text-neutral-400 font-inter text-sm mb-6">Full-color printing on any fabric. No minimums. No per-color charges.</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-neutral-800">
                          <th className="text-left py-3 pr-4 text-xs text-neutral-500 uppercase tracking-widest font-inter">Quantity</th>
                          <th className="text-left py-3 text-xs text-neutral-500 uppercase tracking-widest font-inter">Per Piece (full color)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dtfTiers.map((tier) => (
                          <tr
                            key={tier.range}
                            className={`border-b border-neutral-900 transition-colors hover:bg-neutral-900 ${tier.popular ? "pricing-popular bg-red-900/10 border-red-900/30" : ""}`}
                          >
                            <td className="py-4 pr-4">
                              <span className="font-teko text-xl text-white">{tier.range} <span className="text-neutral-500 font-inter text-sm font-normal">pcs</span></span>
                            </td>
                            <td className="py-4">
                              <span className="font-teko text-2xl text-red-500">{tier.perPiece}</span>
                              <span className="text-neutral-500 font-inter text-xs ml-2">unlimited colors</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-4 flex items-start gap-2 p-4 bg-red-900/10 border border-red-900/30 text-xs text-neutral-400 font-inter">
                    <Info size={14} className="text-red-500 shrink-0 mt-0.5" />
                    DTF works on all fabric types including polyester, cotton, nylon, and blends. Minimum 1 piece. No setup fees. Full-color on any size design.
                  </div>
                </div>
              )}

              {/* Embroidery Pricing */}
              {activeTab === 2 && (
                <div data-testid="embroidery-pricing">
                  <h2 className="font-teko text-3xl text-white uppercase tracking-tight mb-2">Embroidery Pricing</h2>
                  <p className="text-neutral-400 font-inter text-sm mb-6">Minimum 12 pieces. Free digitizing on orders of 24+.</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-neutral-800">
                          <th className="text-left py-3 pr-4 text-xs text-neutral-500 uppercase tracking-widest font-inter">Quantity</th>
                          <th className="text-left py-3 text-xs text-neutral-500 uppercase tracking-widest font-inter">Per Piece</th>
                        </tr>
                      </thead>
                      <tbody>
                        {embroideryTiers.map((tier) => (
                          <tr
                            key={tier.range}
                            className={`border-b border-neutral-900 transition-colors hover:bg-neutral-900 ${tier.popular ? "pricing-popular bg-red-900/10 border-red-900/30" : ""}`}
                          >
                            <td className="py-4 pr-4">
                              <span className="font-teko text-xl text-white">{tier.range} <span className="text-neutral-500 font-inter text-sm font-normal">pcs</span></span>
                            </td>
                            <td className="py-4">
                              <span className="font-teko text-2xl text-red-500">{tier.perPiece}</span>
                              {tier.range === "12-23" && <span className="text-neutral-500 font-inter text-xs ml-2">+$25 digitizing</span>}
                              {tier.range !== "12-23" && <span className="text-green-500 font-inter text-xs ml-2">free digitizing</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Rush Pricing */}
              <div className="mt-10" data-testid="rush-pricing">
                <h2 className="font-teko text-3xl text-white uppercase tracking-tight mb-2">Rush Order Pricing</h2>
                <p className="text-neutral-400 font-inter text-sm mb-5">Add to base per-piece price. Standard turnaround is 7-10 business days.</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {rushPricing.map((r) => (
                    <div key={r.name} className="ninja-card p-5">
                      <div className="font-teko text-lg text-white uppercase tracking-wide mb-1">{r.name}</div>
                      <div className="font-teko text-2xl text-red-500 mb-2">{r.premium}</div>
                      <p className="text-neutral-500 font-inter text-xs">{r.note}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pricing Notes */}
              <div className="mt-8 space-y-2">
                {pricingNotes.map((note, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-neutral-500 font-inter">
                    <div className="w-1 h-1 bg-red-600 rounded-full mt-1.5 shrink-0" />
                    {note}
                  </div>
                ))}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="ninja-card p-6 sticky top-28">
                <div className="red-line" />
                <h3 className="font-teko text-2xl text-white uppercase tracking-wide mb-2">Get an Exact Quote</h3>
                <p className="text-neutral-400 font-inter text-xs mb-4">These are starting prices. For exact pricing on your specific order, submit a quote request — we respond in 2 hours.</p>
                {!showForm ? (
                  <>
                    <button
                      data-testid="pricing-get-quote-btn"
                      onClick={() => setShowForm(true)}
                      className="ninja-btn-primary w-full text-center font-teko tracking-widest py-4 text-lg block"
                    >
                      GET EXACT QUOTE
                    </button>
                    <a href="tel:+12145550190" className="block text-center text-xs text-neutral-500 mt-3 font-inter hover:text-white transition-colors">(214) 555-0190</a>
                  </>
                ) : (
                  <QuoteForm compact />
                )}
              </div>

              {/* Why Choose */}
              <div className="ninja-card p-6">
                <h3 className="font-teko text-xl text-white uppercase tracking-wide mb-4">Why Ninja Print?</h3>
                {["Free 2-hour quote response", "No hidden fees on 24+ orders", "Free DFW delivery", "Pantone color matching", "Rush orders available", "Satisfaction guaranteed"].map(item => (
                  <div key={item} className="flex items-center gap-2 text-sm text-neutral-400 font-inter py-1.5 border-b border-neutral-900 last:border-0">
                    <CheckCircle size={12} className="text-red-500 shrink-0" /> {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
